/**
 * Browser Chase Tracker
 * Manages order chasing in browser mode - monitors orderbook and repositions orders
 * 
 * Key features:
 * - Global singleton - persists across page navigation
 * - Tracks orders by exact size (immutable property)
 * - Uses BrowserPositionTracker for order verification
 * - Persists state to localStorage
 * - Dispatches events for UI updates
 */

export class BrowserChaseTracker {
    constructor() {
        this.activeChases = new Map(); // chaseId -> chaseState
        this.chaseHistory = []; // Completed/stopped chases
        this.orderbookConnections = new Map(); // asset -> subscription status
        this.monitoringIntervals = new Map(); // chaseId -> intervalId
        
        // Restore state from localStorage
        this.restoreState();
        
        // console.log('[CHASE-TRACKER] Initialized');
    }

    /**
     * Start chasing an order
     * @param {string} orderId - Original order ID
     * @param {Object} orderData - Order details from positions tracker
     * @param {Object} settings - Chase settings from modal
     */
    async startChase(orderId, orderData, settings) {
        try {
            const chaseId = `chase-${orderId}-${Date.now()}`;
            
            // Normalize order data (handle both raw API format and transformed format)
            const normalizedData = {
                asset: orderData.coin || orderData.asset,
                side: orderData.side === 'BUY' ? 'B' : (orderData.side === 'SELL' ? 'A' : orderData.side),
                size: parseFloat(orderData.sz || orderData.size),
                price: parseFloat(orderData.limitPx || orderData.price)
            };
            
            // console.log(`[CHASE-TRACKER] Starting chase ${chaseId}`, {
            //     orderId,
            //     asset: normalizedData.asset,
            //     side: normalizedData.side,
            //     size: normalizedData.size,
            //     price: normalizedData.price
            // });
            
            // Cancel any TP/SL orders for this position
            const cancelledTpSl = await this.cancelTpSlOrders(normalizedData.asset, normalizedData.side);
            
            // Create chase state
            const chaseState = {
                chaseId: chaseId,
                originalOid: String(orderId),
                currentOid: String(orderId),
                oidHistory: [String(orderId)],
                asset: normalizedData.asset,
                side: normalizedData.side,
                size: normalizedData.size, // IMMUTABLE - never changes
                originalPrice: normalizedData.price,
                currentPrice: normalizedData.price,
                settings: {
                    tickDistance: settings.tickDistance || 10,
                    frequencyRangeMin: settings.frequencyRangeMin || 10,
                    frequencyRangeMax: settings.frequencyRangeMax || 20,
                    rangePrice: settings.rangePrice || null,
                    rangeType: settings.rangeType || null,
                    anchorPrice: settings.anchorPrice || null,
                    useAnchor: settings.useAnchor || false,
                    tpslEnabled: settings.tpslEnabled || false,
                    tpAtrMultiple: settings.tpAtrMultiple || null,
                    slAtrMultiple: settings.slAtrMultiple || null
                },
                status: 'active',
                lastModification: null,
                modificationCount: 0,
                startTime: Date.now(),
                lastCheck: Date.now(),
                cancelledTpSl: cancelledTpSl, // Store for potential restoration
                // Independent verification: Check every 5s, fail after 3 misses (15s total)
                verificationFailures: 0,        // Count consecutive verification failures
                verificationIntervalId: null    // setInterval ID for cleanup
            };
            
            // Store in active chases
            this.activeChases.set(chaseId, chaseState);
            
            // Save to localStorage
            this.saveState();
            
            // Connect to orderbook for this asset
            await this.connectOrderbook(chaseState.asset);
            
            // Start monitoring loop
            this.startMonitoring(chaseId);
            
            // Start independent verification loop (every 5 seconds)
            this.startVerificationLoop(chaseId);
            
            // Set up event listeners for instant fill/cancel detection
            const fillListener = (event) => {
                const { orderId } = event.detail;
                if (chaseState.oidHistory.includes(String(orderId))) {
                    // console.log(`[CHASE-TRACKER] 🎯 Order ${orderId} filled (detected via event)`);
                    this.stopChase(chaseId, 'order_filled');
                }
            };
            
            const cancelListener = (event) => {
                const { orderId } = event.detail;
                if (chaseState.oidHistory.includes(String(orderId))) {
                    // console.log(`[CHASE-TRACKER] 🎯 Order ${orderId} cancelled (detected via event)`);
                    this.stopChase(chaseId, 'order_cancelled');
                }
            };
            
            window.addEventListener('order-filled', fillListener);
            window.addEventListener('order-cancelled', cancelListener);
            
            // Store listeners for cleanup
            chaseState.fillListener = fillListener;
            chaseState.cancelListener = cancelListener;
            
            // Dispatch event for UI
            window.dispatchEvent(new CustomEvent('chase-started', {
                detail: {
                    chaseId: chaseId,
                    orderId: orderId,
                    orderData: orderData,
                    settings: settings
                }
            }));
            
            // console.log(`[CHASE-TRACKER] ✅ Chase started: ${chaseId}`);
            
            return { success: true, chaseId: chaseId };
            
        } catch (error) {
            // console.error('[CHASE-TRACKER] Error starting chase:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Stop chasing an order
     * @param {string} chaseId - Chase ID to stop
     * @param {string} reason - Reason for stopping
     */
    async stopChase(chaseId, reason = 'user_stopped') {
        const chase = this.activeChases.get(chaseId);
        if (!chase) {
            // console.warn(`[CHASE-TRACKER] Chase not found: ${chaseId}`);
            return { success: false, error: 'Chase not found' };
        }
        
        // console.log(`[CHASE-TRACKER] Stopping chase ${chaseId}, reason: ${reason}`);
        
        // If stopping due to order_filled, verify with MULTIPLE fresh API calls
        if (reason === 'order_filled') {
            // console.log(`[CHASE-TRACKER] 🔍 COMPREHENSIVE VERIFICATION - checking if order truly filled/cancelled...`);
            
            const maxVerificationAttempts = 5;
            let verificationAttempt = 0;
            
            while (verificationAttempt < maxVerificationAttempts) {
                verificationAttempt++;
                // console.log(`[CHASE-TRACKER] Verification attempt ${verificationAttempt}/${maxVerificationAttempts}...`);
                
                // Get fresh orders AND positions from API
                // For HIP-3, must query all DEXs or specific DEX
                const isHip3 = chase.asset.includes(':');
                const freshOrders = isHip3 
                    ? await window.BrowserHyperliquidClient.getAllUserOrders()
                    : await window.BrowserHyperliquidClient.getUserOpenOrders();
                
                // For positions, query specific DEX if HIP-3
                let freshPositions;
                if (isHip3) {
                    const dexName = chase.asset.split(':')[0];
                    const userState = await window.BrowserHyperliquidClient.getUserStateForDex(dexName);
                    freshPositions = userState.assetPositions || [];
                } else {
                    freshPositions = await window.BrowserHyperliquidClient.getUserPositions();
                }
                
                // console.log(`[CHASE-TRACKER] Fresh API: ${freshOrders.length} orders, ${freshPositions.length} positions`);
                
                // Check 1: Is order still in open orders?
                let orderStillExists = false;
                for (const order of freshOrders) {
                    if (order.coin === chase.asset && 
                        parseFloat(order.sz) === parseFloat(chase.size) &&
                        order.side === chase.side) {
                        orderStillExists = true;
                        // console.log(`[CHASE-TRACKER] ✅ Order STILL ACTIVE in open orders! OID: ${order.oid}`);
                        // console.log(`[CHASE-TRACKER] Order: ${order.coin} ${order.side} ${order.sz} @ ${order.limitPx}`);
                        // console.log(`[CHASE-TRACKER] Chase will continue - order is still being chased`);
                        break;
                    }
                }
                
                if (orderStillExists) {
                    // console.log(`[CHASE-TRACKER] ✅ Order verified active - NOT stopping chase, continuing to chase`);
                    return { success: false, error: 'Order still active - chase continues' };
                }
                
                // Check 2: Did order fill into a position?
                let positionExists = false;
                for (const pos of freshPositions) {
                    if (pos.position.coin === chase.asset) {
                        const posSize = Math.abs(parseFloat(pos.position.szi));
                        if (posSize > 0) {
                            positionExists = true;
                            // console.log(`[CHASE-TRACKER] ✅ Found ACTIVE position: ${pos.position.coin} ${pos.position.szi > 0 ? 'LONG' : 'SHORT'} ${posSize}`);
                            break;
                        }
                    }
                }
                
                // If order not in open orders AND not in positions, wait and retry
                if (!orderStillExists && !positionExists) {
                    if (verificationAttempt < maxVerificationAttempts) {
                        // console.log(`[CHASE-TRACKER] ⚠️ Order not in orders OR positions - waiting 500ms before retry ${verificationAttempt}/${maxVerificationAttempts}...`);
                        await new Promise(resolve => setTimeout(resolve, 500));
                        continue;
                    } else {
                        // console.log(`[CHASE-TRACKER] ✅ After ${maxVerificationAttempts} checks: order confirmed filled/cancelled`);
                        break;
                    }
                }
                
                // Order filled into position - safe to stop chase
                if (positionExists) {
                    // console.log(`[CHASE-TRACKER] ✅ Order filled into position - safe to stop chase`);
                    break;
                }
            }
            
            // console.log(`[CHASE-TRACKER] ✅ Verification complete after ${verificationAttempt} attempts`);
        }
        
        // Cancel the order if user manually stopped the chase
        if (reason === 'user_stopped' && chase.currentOid) {
            try {
                // console.log(`[CHASE-TRACKER] Cancelling order ${chase.currentOid} on user stop...`);
                const cancelResult = await window.BrowserHyperliquidClient.cancelOrder({
                    orderId: chase.currentOid,
                    asset: chase.asset
                });
                if (cancelResult.success) {
                    // console.log(`[CHASE-TRACKER] ✅ Order ${chase.currentOid} cancelled on stop`);
                } else {
                    // console.warn(`[CHASE-TRACKER] ⚠️ Failed to cancel order ${chase.currentOid}:`, cancelResult.error);
                }
            } catch (error) {
                // console.error(`[CHASE-TRACKER] Error cancelling order on stop:`, error);
            }
        }

        // Place TP/SL orders when order fills (if ATR multiples are configured)
        if (reason === 'order_filled' && chase.settings?.tpslEnabled && chase.settings?.tpAtrMultiple && chase.settings?.slAtrMultiple) {
            try {
                await this.placeTPSLOnFill(chase);
            } catch (error) {
                // console.error(`[CHASE-TRACKER] Error placing TP/SL on fill:`, error);
            }
        }

        // Update status
        chase.status = 'stopped';
        chase.endTime = Date.now();
        chase.endReason = reason;

        // Stop monitoring
        this.stopMonitoring(chaseId);
        
        // Move to history
        this.chaseHistory.push(chase);
        this.activeChases.delete(chaseId);
        
        // Disconnect orderbook if no other chases for this asset
        this.disconnectOrderbookIfUnused(chase.asset);
        
        // Save state
        this.saveState();
        
        // Dispatch event
        window.dispatchEvent(new CustomEvent('chase-stopped', {
            detail: {
                chaseId: chaseId,
                reason: reason,
                finalOid: chase.currentOid,
                modCount: chase.modificationCount
            }
        }));
        
        // console.log(`[CHASE-TRACKER] ✅ Chase stopped: ${chaseId}`);
        
        return { success: true };
    }

    /**
     * Connect to orderbook WebSocket for an asset
     * Uses dedicated ChaseOrderbookService (independent of terminal)
     */
    async connectOrderbook(asset) {
        // ChaseOrderbookService handles multiple subscriptions per asset automatically
        // No need to track connections - service does it internally
        // console.log(`[CHASE-TRACKER] Orderbook connection managed by ChaseOrderbookService for ${asset}`);
    }

    /**
     * Disconnect orderbook if no active chases need it
     * ChaseOrderbookService auto-cleanup when no listeners
     */
    disconnectOrderbookIfUnused(asset) {
        // ChaseOrderbookService automatically closes WebSocket when last listener unsubscribes
        // console.log(`[CHASE-TRACKER] Orderbook cleanup managed by ChaseOrderbookService for ${asset}`);
    }

    /**
     * Start monitoring loop for a chase
     */
    async startMonitoring(chaseId) {
        const chase = this.activeChases.get(chaseId);
        if (!chase) return;
        
        // Create listener for this chase
        const orderbookListener = (data) => {
            // Data format: { asset, bids, asks, bestBid, bestAsk, timestamp }
            this.handleOrderbookUpdate(chaseId, data);
        };
        
        // Store listener reference for cleanup
        chase.orderbookListener = orderbookListener;
        
        // Subscribe to dedicated chase orderbook service
        await window.ChaseOrderbookService.subscribe(chase.asset, orderbookListener);
        
        // console.log(`[CHASE-TRACKER] ✅ Subscribed to chase orderbook for ${chase.asset}`);
        // console.log(`[CHASE-TRACKER] Monitoring started for ${chaseId}`);
    }

    /**
     * Stop monitoring loop for a chase
     */
    async stopMonitoring(chaseId) {
        const chase = this.activeChases.get(chaseId);
        if (!chase) return;
        
        // Unsubscribe from chase orderbook service
        if (chase.orderbookListener) {
            await window.ChaseOrderbookService.unsubscribe(chase.asset, chase.orderbookListener);
            chase.orderbookListener = null;
            // console.log(`[CHASE-TRACKER] ✅ Unsubscribed from chase orderbook for ${chase.asset}`);
        }
        
        // Remove event listeners for fill/cancel detection
        if (chase.fillListener) {
            window.removeEventListener('order-filled', chase.fillListener);
            chase.fillListener = null;
        }
        if (chase.cancelListener) {
            window.removeEventListener('order-cancelled', chase.cancelListener);
            chase.cancelListener = null;
        }
        
        // Stop independent verification loop
        if (chase.verificationIntervalId) {
            clearInterval(chase.verificationIntervalId);
            chase.verificationIntervalId = null;
        }
        
        // console.log(`[CHASE-TRACKER] Monitoring stopped for ${chaseId}`);
    }

    /**
     * Handle orderbook update
     */
    async handleOrderbookUpdate(chaseId, orderbookData) {
        const chase = this.activeChases.get(chaseId);
        if (!chase || chase.status !== 'active') {
            // console.log(`[CHASE-DEBUG] handleOrderbookUpdate early return - chase exists: ${!!chase}, status: ${chase?.status}`);
            return;
        }

        // Range check removed - anchor constraint is now applied during order modification, not here

        // Calculate tick size from orderbook prices on first update
        if (!chase.tickSize && orderbookData.bestBid && orderbookData.bestAsk) {
            const tickSize = this.calculateTickSizeFromOrderbook(orderbookData);
            
            if (tickSize) {
                chase.tickSize = tickSize;
                // console.log(`[CHASE-DEBUG] Tick size set: ${tickSize}`);
                // console.log(`[CHASE-TRACKER] Tick size set for ${chase.asset}: $${chase.tickSize}`);
            } else {
                // console.error(`[CHASE-TRACKER] Failed to calculate tick size for ${chase.asset}`);
            }
        }
        
        // Initialize next check time if not set
        if (!chase.nextCheckTime) {
            chase.nextCheckTime = Date.now() + this.getRandomInterval(chase.settings);
            // console.log(`[CHASE-TRACKER] Initial check scheduled in ${((chase.nextCheckTime - Date.now()) / 1000).toFixed(1)}s`);
        }
        
        // Only check when timer expires
        const now = Date.now();
        if (now < chase.nextCheckTime) {
            return; // Not time yet
        }
        
        // console.log(`[CHASE-TRACKER] ⏰ Timer expired, checking order position for ${chase.asset}`);
        
        try{
            // Analyze order position
            const analysis = this.analyzeOrderPosition(chase, orderbookData);
            
            // console.log(`[CHASE-TRACKER] Analysis for ${chase.asset}:`, {
            //     position: analysis.position,
            //     ticksAway: analysis.ticksAway,
            //     isValid: analysis.isValid,
            //     bestPrice: analysis.bestPrice,
            //     currentPrice: chase.currentPrice,
            //     tickDistance: chase.settings.tickDistance
            // });
            
            // Check if modification needed
            if (this.shouldModifyOrder(chase, analysis)) {
                // console.log(`[CHASE-TRACKER] 🔄 Modification triggered for ${chase.asset}`);
                await this.modifyOrder(chase, analysis, orderbookData);
            }
            
            // Roll new random interval for next check
            const nextInterval = this.getRandomInterval(chase.settings);
            chase.nextCheckTime = Date.now() + nextInterval;
            // console.log(`[CHASE-TRACKER] Next check scheduled in ${(nextInterval / 1000).toFixed(1)}s`);
            
        } catch (error) {
            // console.error(`[CHASE-TRACKER] Error handling orderbook update for ${chaseId}:`, error);
            // Roll new interval even on error
            const nextInterval = this.getRandomInterval(chase.settings);
            chase.nextCheckTime = Date.now() + nextInterval;
        }
    }

    /**
     * Analyze order's position in the orderbook
     */
    analyzeOrderPosition(chase, orderbookData) {
        // console.log(`[CHASE-DEBUG] analyzeOrderPosition called for ${chase.asset}`);
        // console.log(`[CHASE-DEBUG] Orderbook data:`, {
        //     hasBids: !!orderbookData?.bids,
        //     hasAsks: !!orderbookData?.asks,
        //     bestBid: orderbookData?.bestBid,
        //     bestAsk: orderbookData?.bestAsk,
        //     bidsLength: orderbookData?.bids?.length,
        //     asksLength: orderbookData?.asks?.length
        // });
        
        const isBuy = chase.side === 'B' || chase.side === 'buy';
        const levels = isBuy ? orderbookData.bids : orderbookData.asks;
        
        if (!levels || levels.length === 0) {
            // console.log(`[CHASE-TRACKER] ❌ Invalid orderbook data:`, {
            //     hasBids: !!orderbookData?.bids,
            //     hasAsks: !!orderbookData?.asks,
            //     bidsLength: orderbookData?.bids?.length,
            //     asksLength: orderbookData?.asks?.length,
            //     isBuy: isBuy,
            //     side: chase.side,
            //     asset: chase.asset
            // });
            return { position: 999, ticksAway: 999, bestPrice: null, isValid: false };
        }
        
        // Handle both array [price, size] and object {px, sz} formats
        const getPrice = (level) => {
            if (Array.isArray(level)) return parseFloat(level[0]);
            return parseFloat(level.px);
        };
        
        const bestPrice = getPrice(levels[0]);
        const currentPrice = chase.currentPrice;
        
        // Find order's position in the queue
        let position = 0;
        let ticksAway = 0;
        let foundOrder = false;
        
        for (let i = 0; i < levels.length; i++) {
            const levelPrice = getPrice(levels[i]);
            
            if (isBuy) {
                // For buy orders: higher price = better position
                if (levelPrice > currentPrice) {
                    position++;
                } else if (Math.abs(levelPrice - currentPrice) < 0.00000001) {
                    // Found our order's price level
                    position++;
                    foundOrder = true;
                    break;
                } else {
                    // Our order is behind this level
                    foundOrder = true;
                    break;
                }
            } else {
                // For sell orders: lower price = better position
                if (levelPrice < currentPrice) {
                    position++;
                } else if (Math.abs(levelPrice - currentPrice) < 0.00000001) {
                    // Found our order's price level
                    position++;
                    foundOrder = true;
                    break;
                } else {
                    // Our order is behind this level
                    foundOrder = true;
                    break;
                }
            }
        }
        
        // Calculate ticks away from best price
        ticksAway = position > 0 ? position - 1 : 0;
        
        return {
            position: position,
            ticksAway: ticksAway,
            bestPrice: bestPrice,
            isValid: true
        };
    }

    /**
     * Check if order should be modified
     */
    shouldModifyOrder(chase, analysis) {
        // console.log(`[CHASE-DEBUG] shouldModifyOrder called - chaseId: ${chase.chaseId}, asset: ${chase.asset}`);
        // console.log(`[CHASE-DEBUG] Chase object keys:`, Object.keys(chase));
        // console.log(`[CHASE-DEBUG] chase.tickSize value: ${chase.tickSize}, type: ${typeof chase.tickSize}`);
        
        // Skip if already modifying (prevent parallel modifications)
        if (chase.isModifying) {
            // console.log(`[CHASE-DEBUG] ⏸️ Already modifying, skipping this check`);
            return false;
        }
        
        // Validate analysis data
        if (!analysis.isValid || analysis.position === 999 || analysis.ticksAway === 999) {
            // console.log(`[CHASE-DEBUG] ❌ Invalid analysis - isValid: ${analysis.isValid}, position: ${analysis.position}, ticksAway: ${analysis.ticksAway}`);
            // console.log(`[CHASE-TRACKER] Invalid analysis data, skipping modification`);
            return false;
        }
        
        // Ensure tick size is calculated
        if (!chase.tickSize) {
            // console.log(`[CHASE-DEBUG] ❌ No tick size - chase.tickSize: ${chase.tickSize}`);
            // console.log(`[CHASE-TRACKER] Tick size not calculated yet, skipping modification`);
            return false;
        }
        
        // console.log(`[CHASE-DEBUG] ✅ Passed initial checks, calculating distance...`);
        
        // Calculate actual distance from best price in ticks
        const isBuy = chase.side === 'B' || chase.side === 'buy';
        const bestPrice = isBuy ? analysis.bestPrice : analysis.bestPrice; // bestBid for buy, bestAsk for sell
        
        // console.log(`[CHASE-DEBUG] Distance calculation inputs:`, {
        //     isBuy: isBuy,
        //     side: chase.side,
        //     bestPrice: bestPrice,
        //     currentPrice: chase.currentPrice,
        //     tickSize: chase.tickSize,
        //     priceDiff: Math.abs(bestPrice - chase.currentPrice)
        // });
        
        const actualDistance = Math.abs(bestPrice - chase.currentPrice) / chase.tickSize;
        const targetDistance = chase.settings.tickDistance;
        
        // console.log(`[CHASE-DEBUG] actualDistance calculated: ${actualDistance}, isNaN: ${isNaN(actualDistance)}, isFinite: ${isFinite(actualDistance)}`);
        
        // Allow 2 tick tolerance before modifying
        const tolerance = 2;
        const drift = Math.abs(actualDistance - targetDistance);
        
        // console.log(`[CHASE-DEBUG] Drift calculation: actualDistance=${actualDistance}, targetDistance=${targetDistance}, drift=${drift}`);
        
        // console.log(`[CHASE-TRACKER] Distance check:`, {
        //     actualDistance: actualDistance.toFixed(1),
        //     targetDistance: targetDistance,
        //     drift: drift.toFixed(1),
        //     tolerance: tolerance,
        //     needsModification: drift > tolerance
        // });
        
        // Only modify if we've drifted more than tolerance
        if (drift <= tolerance) {
            // console.log(`[CHASE-DEBUG] ❌ Drift within tolerance: ${drift} <= ${tolerance}`);
            return false;
        }
        
        // console.log(`[CHASE-DEBUG] ✅ Drift exceeds tolerance: ${drift} > ${tolerance}`);
        
        // No frequency check needed - timer already handles this
        
        // console.log(`[CHASE-DEBUG] ✅✅✅ ALL CHECKS PASSED - SHOULD MODIFY ORDER`);
        return true;
    }

    /**
     * Start independent verification loop
     * Checks BrowserPositionTracker every 5s, stops chase after 3 consecutive failures
     */
    startVerificationLoop(chaseId) {
        const chase = this.activeChases.get(chaseId);
        if (!chase) return;
        
        // Check every 5 seconds
        chase.verificationIntervalId = setInterval(() => {
            // Get chase again (it might have been stopped)
            const currentChase = this.activeChases.get(chaseId);
            if (!currentChase || currentChase.status !== 'active') {
                // Chase stopped, clear interval
                if (chase.verificationIntervalId) {
                    clearInterval(chase.verificationIntervalId);
                    chase.verificationIntervalId = null;
                }
                return;
            }
            
            // Check if order exists in BrowserPositionTracker orders
            let orderFound = false;
            
            if (window.BrowserPositionTracker?.orders) {
                const orders = Array.from(window.BrowserPositionTracker.orders.values());
                orderFound = orders.some(o => 
                    o.coin === currentChase.asset &&
                    o.side === currentChase.side &&
                    Math.abs(parseFloat(o.sz || o.size) - currentChase.size) < 0.00000001
                );
            }
            
            if (orderFound) {
                // Order exists - reset failure counter
                currentChase.verificationFailures = 0;
                // console.log(`[CHASE-TRACKER] ✅ Order verified for ${currentChase.asset}`);
            } else {
                // Order not found - increment failure counter
                currentChase.verificationFailures++;
                // console.log(`[CHASE-TRACKER] ⚠️ Order not found for ${currentChase.asset} (attempt ${currentChase.verificationFailures}/3)`);
                
                // After 3 consecutive failures (15 seconds), stop chase
                if (currentChase.verificationFailures >= 3) {
                    // console.log(`[CHASE-TRACKER] 🛑 Order ${currentChase.asset} not found after 3 checks (15s), stopping chase`);
                    this.stopChase(chaseId, 'order_filled');
                }
            }
        }, 5000); // Check every 5 seconds
        
        // console.log(`[CHASE-TRACKER] ✅ Started verification loop for ${chaseId}`);
    }

    /**
     * Modify order (cancel + recreate)
     */
    async modifyOrder(chase, analysis, orderbookData) {
        // Set modification lock
        chase.isModifying = true;
        
        try {
            // console.log(`[CHASE-DEBUG] ═══════════════════════════════════════`);
            // console.log(`[CHASE-DEBUG] MODIFY ORDER STARTED`);
            // console.log(`[CHASE-DEBUG] ═══════════════════════════════════════`);
            // console.log(`[CHASE-TRACKER] Modifying order for chase ${chase.chaseId}`);
            // console.log(`[CHASE-TRACKER] Current position: ${analysis.position}, ticks away: ${analysis.ticksAway}`);
            
            // STEP 1: Verify order still exists by ASSET + SIZE + SIDE (not OID)
            // console.log(`[CHASE-DEBUG] STEP 1: Verifying order exists with FRESH API call...`);
            // console.log(`[CHASE-TRACKER] Looking for order: ${chase.asset} ${chase.side} ${chase.size}`);
            
            // Find order by asset, size, and side (OID changes on each modification)
            // Retry up to 3 times with delays to allow API to update
            let order = null;
            let attempts = 0;
            const maxAttempts = 3;
            
            // console.log(`[CHASE-DEBUG] Using fresh API calls (not cached tracker) - side format: '${chase.side}'`);
            
            while (!order && attempts < maxAttempts) {
                attempts++;
                
                if (attempts > 1) {
                    // console.log(`[CHASE-DEBUG] Retry attempt ${attempts}/${maxAttempts}, waiting 500ms...`);
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
                
                // Get fresh orders from API
                // For HIP-3 assets, query only the specific DEX (2 API calls)
                // For regular assets, query main DEX only (2 API calls)
                const isHip3 = chase.asset.includes(':');
                let freshOrders;
                
                if (isHip3) {
                    // Extract DEX name from asset (e.g., "xyz:PERP" -> "xyz")
                    const dexName = chase.asset.split(':')[0];
                    // Direct API call to specific DEX only
                    freshOrders = await window.BrowserHyperliquidClient.infoClient.openOrders({ 
                        user: window.BrowserHyperliquidClient.publicAddress, 
                        dex: dexName 
                    });
                } else {
                    // Regular asset - main DEX only
                    freshOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
                }
                // console.log(`[CHASE-DEBUG] Fresh API returned ${freshOrders?.length || 0} total open orders (HIP-3: ${isHip3})`);
                
                for (const apiOrder of freshOrders) {
                    if (apiOrder.coin === chase.asset && 
                        parseFloat(apiOrder.sz) === parseFloat(chase.size) &&
                        apiOrder.side === chase.side) {
                        order = apiOrder;
                        // console.log(`[CHASE-DEBUG] Found matching order with OID: ${apiOrder.oid}`);
                        break;
                    }
                }
            }
            
            // console.log(`[CHASE-DEBUG] Order lookup result: ${order ? 'FOUND' : 'NOT FOUND'} (after ${attempts} attempts)`);
            // console.log(`[CHASE-DEBUG] Search criteria: asset=${chase.asset}, size=${chase.size}, side=${chase.side}`);
            // console.log(`[CHASE-DEBUG] Fresh API total orders: ${(await window.BrowserHyperliquidClient.getUserOpenOrders()).length}`);
            
            if (!order) {
                // console.log(`[CHASE-DEBUG] ❌ Order no longer exists after ${attempts} attempts, attempting to stop chase`);
                // console.log(`[CHASE-TRACKER] Order ${chase.asset} ${chase.side} ${chase.size} no longer exists - attempting to stop chase`);
                const stopResult = await this.stopChase(chase.chaseId, 'order_filled');
                
                if (!stopResult.success) {
                    // console.log(`[CHASE-TRACKER] ⚠️ stopChase verification found order still exists, continuing chase`);
                    // Order actually still exists, don't terminate
                    return;
                }
                
                // console.log(`[CHASE-TRACKER] ✅ Chase stopped and verified`);
                return;
            }
            
            // Extract OID from found order for cancellation
            const currentOid = order.oid;
            // console.log(`[CHASE-DEBUG] ✅ Order verified, OID: ${currentOid}, proceeding to price calculation`);
            
            // STEP 2: Calculate new price using tick distance formula
            // console.log(`[CHASE-DEBUG] STEP 2: Calculating new price...`);
            const isBuy = chase.side === 'B' || chase.side === 'buy';
            
            // Ensure tick size is calculated
            if (!chase.tickSize) {
                // console.log(`[CHASE-DEBUG] ❌ CRITICAL: No tick size in modifyOrder`);
                // console.error(`[CHASE-TRACKER] ❌ Tick size not calculated yet, cannot modify order`);
                return;
            }
            
            // Get best bid/ask from orderbook data
            const bestBid = parseFloat(orderbookData.bestBid);
            const bestAsk = parseFloat(orderbookData.bestAsk);
            
            // console.log(`[CHASE-DEBUG] Orderbook prices - bestBid: ${bestBid}, bestAsk: ${bestAsk}`);
            
            if (!bestBid || !bestAsk) {
                // console.log(`[CHASE-DEBUG] ❌ Missing orderbook data`);
                // console.error(`[CHASE-TRACKER] ❌ No best bid/ask in orderbook data`);
                return;
            }
            
            // Get asset index to cache szDecimals for proper rounding
            await window.BrowserHyperliquidClient.getAssetIndex(chase.asset);
            
            // Calculate target price: tickDistance ticks away from current market price
            const currentMarketPrice = isBuy ? bestBid : bestAsk;
            let rawTarget;
            if (isBuy) {
                // For buy orders: tickDistance ticks BELOW market
                rawTarget = currentMarketPrice - (chase.settings.tickDistance * chase.tickSize);
                // console.log(`[CHASE-DEBUG] BUY calculation: ${currentMarketPrice} - (${chase.settings.tickDistance} * ${chase.tickSize}) = ${rawTarget}`);
            } else {
                // For sell orders: tickDistance ticks ABOVE market
                rawTarget = currentMarketPrice + (chase.settings.tickDistance * chase.tickSize);
                // console.log(`[CHASE-DEBUG] SELL calculation: ${currentMarketPrice} + (${chase.settings.tickDistance} * ${chase.tickSize}) = ${rawTarget}`);
            }
            
            // Round to valid tick size (uses cached szDecimals from getAssetIndex)
            const newPrice = window.BrowserHyperliquidClient.roundToTickSize(rawTarget);
            
            // console.log(`[CHASE-DEBUG] Price after rounding: ${newPrice}`);
            // console.log(`[CHASE-TRACKER] Price calculation:`, {
            //     side: isBuy ? 'BUY' : 'SELL',
            //     bestBid: bestBid,
            //     bestAsk: bestAsk,
            //     tickSize: chase.tickSize,
            //     tickDistance: chase.settings.tickDistance,
            //     rawTarget: rawTarget,
            //     newPrice: newPrice,
            //     oldPrice: chase.currentPrice,
            //     priceChange: (newPrice - chase.currentPrice).toFixed(6)
            // });
            
            // Check anchor constraint (EV Grid feature)
            if (chase.settings.useAnchor && chase.settings.anchorPrice) {
                const anchorPrice = chase.settings.anchorPrice;
                
                if (isBuy && newPrice >= anchorPrice) {
                    // BUY order would cross above anchor - skip this modification
                    // console.log(`[CHASE-TRACKER] BUY order ${newPrice} would cross anchor ${anchorPrice}, skipping modification`);
                    return; // Don't place order, but keep chase alive
                }
                
                if (!isBuy && newPrice <= anchorPrice) {
                    // SELL order would cross below anchor - skip this modification
                    // console.log(`[CHASE-TRACKER] SELL order ${newPrice} would cross anchor ${anchorPrice}, skipping modification`);
                    return; // Don't place order, but keep chase alive
                }
            }
            
            // STEP 3: Cancel old order (using OID from the order we just found)
            // console.log(`[CHASE-DEBUG] STEP 3: Canceling old order...`);
            // console.log(`[CHASE-TRACKER] Canceling order OID: ${currentOid} (type: ${typeof currentOid})`);
            // console.log(`[CHASE-TRACKER] Order details: ${chase.asset} ${chase.side} ${chase.size}`);
            
            const cancelResult = await window.BrowserHyperliquidClient.cancelOrder({
                orderId: currentOid,
                asset: chase.asset
            });
            
            // console.log(`[CHASE-DEBUG] Cancel result:`, cancelResult);
            // console.log(`[CHASE-DEBUG] Cancel success: ${cancelResult.success}`);
            
            if (!cancelResult.success) {
                // console.log(`[CHASE-DEBUG] ❌ Cancel failed`);
                // console.error(`[CHASE-TRACKER] Cancel failed:`, cancelResult.error);
                
                // Check if order was already filled (error might be string or object)
                const errorStr = typeof cancelResult.error === 'string' 
                    ? cancelResult.error 
                    : JSON.stringify(cancelResult.error);
                
                if (errorStr && errorStr.toLowerCase().includes('filled')) {
                    // console.log(`[CHASE-TRACKER] Cancel failed because order was filled - stopping chase`);
                    await this.stopChase(chase.chaseId, 'order_filled');
                }
                return;
            }
            
            // console.log(`[CHASE-DEBUG] ✅ Order canceled successfully`);
            // console.log(`[CHASE-TRACKER] ✅ Order canceled`);
            
            // STEP 4: Create new order at new price
            // console.log(`[CHASE-DEBUG] STEP 4: Creating new order...`);
            // console.log(`[CHASE-TRACKER] Creating new order at ${newPrice}...`);
            const newOrderResult = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset: chase.asset,
                orderSide: isBuy ? 'buy' : 'sell',
                size: chase.size,
                price: newPrice,
                orderType: 'limit',
                timeInForce: 'GTC',
                reduceOnly: false
            });
            
            // console.log(`[CHASE-DEBUG] New order result:`, newOrderResult);
            
            if (!newOrderResult.success) {
                // console.log(`[CHASE-DEBUG] ❌ Failed to create new order`);
                // console.error(`[CHASE-TRACKER] ❌ Failed to recreate order:`, newOrderResult.error);
                await this.stopChase(chase.chaseId, 'modification_failed');
                return;
            }
            
            // console.log(`[CHASE-DEBUG] ✅ New order created successfully`);
            
            // STEP 5: Verify new order was created
            // console.log(`[CHASE-DEBUG] STEP 5: Verifying new order creation...`);
            const newOid = newOrderResult.result?.response?.data?.statuses?.[0]?.resting?.oid;
            
            // console.log(`[CHASE-DEBUG] New OID from response: ${newOid}`);
            
            if (!newOid) {
                // console.log(`[CHASE-DEBUG] ❌ No OID in response`);
                // console.error(`[CHASE-TRACKER] ❌ No OID in response`, newOrderResult);
                await this.stopChase(chase.chaseId, 'modification_failed');
                return;
            }
            
            // console.log(`[CHASE-TRACKER] ✅ New order created successfully`);
            
            // STEP 6: Update chase state (only price and counters, NOT OID)
            // console.log(`[CHASE-DEBUG] STEP 6: Updating chase state...`);
            chase.currentPrice = newPrice;
            chase.lastModification = Date.now();
            chase.modificationCount++;
            
            // console.log(`[CHASE-DEBUG] Chase state updated - newPrice: ${newPrice}, modCount: ${chase.modificationCount}`);
            
            // STEP 7: Save state
            // console.log(`[CHASE-DEBUG] STEP 7: Saving state...`);
            this.saveState();
            
            // STEP 7.5: Poll specific DEX to immediately get new order
            // Prevents order from disappearing for 0-15 seconds until next poll
            // Detect HIP-3 vs Main to poll correct DEX only (efficient 2 API calls)
            const isHip3 = chase.asset.includes(':');
            const dexName = isHip3 ? chase.asset.split(':')[0] : 'main';
            
            if (window.BrowserPositionTracker) {
                // Don't await - let it run async to not slow down chase logic
                // Poll position data to sync state
                window.BrowserPositionTracker.pollData().catch(err => {
                    // console.error(`[CHASE-TRACKER] Poll after modification failed:`, err);
                });
            }
            
            // STEP 8: Dispatch event
            // console.log(`[CHASE-DEBUG] STEP 8: Dispatching event...`);
            window.dispatchEvent(new CustomEvent('chase-order-modified', {
                detail: {
                    chaseId: chase.chaseId,
                    oldOid: currentOid,
                    newOid: newOid,
                    newPrice: newPrice,
                    modCount: chase.modificationCount
                }
            }));
            
            // console.log(`[CHASE-TRACKER] ✅ Order modified successfully (mod #${chase.modificationCount})`);
            
        } catch (error) {
            // console.log(`[CHASE-DEBUG] ❌❌❌ EXCEPTION in modifyOrder:`, error);
            // console.error(`[CHASE-TRACKER] Error modifying order:`, error);
            // console.error(error.stack);
            
            // Check if error indicates order was filled
            if (error.message && error.message.includes('filled')) {
                await this.stopChase(chase.chaseId, 'order_filled');
            }
        } finally {
            // Release modification lock
            chase.isModifying = false;
        }
        
        // console.log(`[CHASE-DEBUG] ═══════════════════════════════════════`);
        // console.log(`[CHASE-DEBUG] MODIFY ORDER COMPLETED`);
        // console.log(`[CHASE-DEBUG] ═══════════════════════════════════════`);
    }

    /**
     * Get active chase for an order (by OID or size matching)
     */
    getChaseForOrder(order) {
        // EXCLUDE reduce-only orders (TP/SL) from chase highlighting
        if (order.reduceOnly || order.isPositionTpsl) {
            return null;
        }
        
        const orderOid = String(order.oid);
        const orderAsset = order.coin || order.asset;
        const orderSize = parseFloat(order.sz || order.size);
        
        // Normalize side (handle both 'B'/'A' and 'BUY'/'SELL')
        let orderSide = order.side;
        if (orderSide === 'BUY') orderSide = 'B';
        if (orderSide === 'SELL') orderSide = 'A';
        
        // Strategy 1: Exact current OID match
        for (const [chaseId, chase] of this.activeChases.entries()) {
            if (String(chase.currentOid) === orderOid) {
                return { chaseId, chase, method: 'exact_oid' };
            }
        }
        
        // Strategy 2: Historical OID match
        for (const [chaseId, chase] of this.activeChases.entries()) {
            if (chase.oidHistory.includes(orderOid)) {
                return { chaseId, chase, method: 'historical_oid' };
            }
        }
        
        // Strategy 3: Asset + Side + Size match (MOST RELIABLE)
        for (const [chaseId, chase] of this.activeChases.entries()) {
            const assetMatch = chase.asset === orderAsset;
            const sideMatch = chase.side === orderSide;
            const sizeMatch = Math.abs(chase.size - orderSize) < 0.00000001;
            
            if (assetMatch && sideMatch && sizeMatch) {
                // Update OID history
                if (!chase.oidHistory.includes(orderOid)) {
                    chase.oidHistory.push(orderOid);
                    chase.currentOid = orderOid;
                    this.saveState();
                }
                return { chaseId, chase, method: 'asset_side_size' };
            }
        }
        
        return null;
    }

    /**
     * Save state to localStorage
     */
    saveState() {
        try {
            // Save active chases
            const chasesObj = {};
            for (const [chaseId, chase] of this.activeChases.entries()) {
                // Remove non-serializable properties (functions)
                const { orderbookListener, fillListener, cancelListener, ...serializable } = chase;
                chasesObj[chaseId] = serializable;
            }
            localStorage.setItem('activeChases', JSON.stringify(chasesObj));
            
            // Save history (keep last 50)
            const recentHistory = this.chaseHistory.slice(-50);
            localStorage.setItem('chaseHistory', JSON.stringify(recentHistory));
            
        } catch (error) {
            // console.error('[CHASE-TRACKER] Error saving state:', error);
        }
    }

    /**
     * Restore state from localStorage
     */
    restoreState() {
        try {
            // Restore active chases
            const saved = localStorage.getItem('activeChases');
            if (saved) {
                const chasesObj = JSON.parse(saved);
                for (const [chaseId, chase] of Object.entries(chasesObj)) {
                    this.activeChases.set(chaseId, chase);
                    
                    // Re-create event listeners for instant fill/cancel detection
                    const fillListener = (event) => {
                        const { orderId } = event.detail;
                        if (chase.oidHistory && chase.oidHistory.includes(String(orderId))) {
                            this.stopChase(chaseId, 'order_filled');
                        }
                    };
                    
                    const cancelListener = (event) => {
                        const { orderId } = event.detail;
                        if (chase.oidHistory && chase.oidHistory.includes(String(orderId))) {
                            this.stopChase(chaseId, 'order_cancelled');
                        }
                    };
                    
                    window.addEventListener('order-filled', fillListener);
                    window.addEventListener('order-cancelled', cancelListener);
                    
                    // Store listeners for cleanup
                    chase.fillListener = fillListener;
                    chase.cancelListener = cancelListener;
                    
                    // Reset verification state
                    chase.verificationFailures = 0;
                    chase.verificationIntervalId = null;
                    
                    // Reconnect to orderbook
                    this.connectOrderbook(chase.asset);
                    
                    // Resume monitoring
                    this.startMonitoring(chaseId);
                    
                    // Restart independent verification loop
                    this.startVerificationLoop(chaseId);
                }
                // console.log(`[CHASE-TRACKER] Restored ${this.activeChases.size} active chases`);
            }
            
            // Restore history
            const history = localStorage.getItem('chaseHistory');
            if (history) {
                this.chaseHistory = JSON.parse(history);
                // console.log(`[CHASE-TRACKER] Restored ${this.chaseHistory.length} chase history entries`);
            }
        } catch (error) {
            // console.error('[CHASE-TRACKER] Error restoring state:', error);
        }
    }

    /**
     * Get status of all active chases
     */
    getStatus() {
        const chases = {};
        for (const [chaseId, chase] of this.activeChases.entries()) {
            chases[chaseId] = {
                chaseId: chase.chaseId,
                asset: chase.asset,
                currentOid: chase.currentOid,
                modCount: chase.modificationCount,
                status: chase.status
            };
        }
        return {
            success: true,
            activeChases: chases,
            count: this.activeChases.size
        };
    }

    /**
     * Cancel TP/SL orders for a position
     * @param {string} asset - Asset symbol
     * @param {string} side - Order side ('B' or 'A')
     * @returns {Array} - Array of cancelled order IDs
     */
    async cancelTpSlOrders(asset, side) {
        const cancelled = [];
        
        try {
            // Get fresh order data
            await window.BrowserPositionTracker.pollData();
            
            // Find TP/SL orders for this position
            const orders = Array.from(window.BrowserPositionTracker.orders.values());
            const tpSlOrders = orders.filter(order => {
                const orderAsset = order.coin || order.asset;
                const orderSide = order.side; // 'BUY' or 'SELL' from position-tracker
                const isReduceOnly = order.reduceOnly || order.orderType === 'TP' || order.orderType === 'SL';
                
                // Match asset and opposite side (TP/SL are reduce-only on opposite side)
                // side param is 'B' or 'A', need to convert to 'BUY'/'SELL' for comparison
                const oppositeSide = side === 'B' ? 'SELL' : 'BUY';
                return orderAsset === asset && orderSide === oppositeSide && isReduceOnly;
            });
            
            // console.log(`[CHASE-TRACKER] Found ${tpSlOrders.length} TP/SL orders to cancel for ${asset}`);
            
            // Cancel each TP/SL order
            for (const order of tpSlOrders) {
                const orderId = order.oid;
                // console.log(`[CHASE-TRACKER] Cancelling TP/SL order ${orderId}...`);
                
                const result = await window.BrowserHyperliquidClient.cancelOrder({
                    orderId: orderId,
                    asset: asset
                });
                
                if (result.success) {
                    cancelled.push({
                        oid: orderId,
                        price: order.limitPx || order.price,
                        type: order.orderType
                    });
                    // console.log(`[CHASE-TRACKER] ✅ Cancelled TP/SL order ${orderId}`);
                } else {
                    // console.error(`[CHASE-TRACKER] ❌ Failed to cancel TP/SL order ${orderId}:`, result.error);
                }
            }
            
        } catch (error) {
            // console.error('[CHASE-TRACKER] Error cancelling TP/SL orders:', error);
        }
        
        return cancelled;
    }

    /**
     * Place TP/SL orders when chase order fills
     * Uses ATR multiples from chase settings
     */
    async placeTPSLOnFill(chase) {
        try {
            const { asset, side, size, settings } = chase;
            const isBuy = side === 'B' || side === 'buy';

            // Get entry price from the filled position
            let entryPrice = chase.currentPrice; // Last known price of the chase order

            // Try to get actual entry from position
            try {
                const positions = await window.BrowserHyperliquidClient.getUserPositions();
                const position = positions.find(p => p.position?.coin === asset);
                if (position && position.position?.entryPx) {
                    entryPrice = parseFloat(position.position.entryPx);
                }
            } catch (e) {
                // console.warn('[CHASE-TRACKER] Could not fetch position entry price, using chase price');
            }

            if (!entryPrice || entryPrice <= 0) {
                // console.error('[CHASE-TRACKER] No valid entry price for TP/SL placement');
                return;
            }

            // Get ATR for calculations
            let atr = null;
            if (window.ATRService) {
                const atrData = await window.ATRService.getATR(asset);
                if (atrData && atrData.atr) {
                    atr = atrData.atr;
                }
            }

            if (!atr) {
                // Fallback: use 1.5% of price as ATR proxy
                atr = entryPrice * 0.015;
                // console.warn(`[CHASE-TRACKER] ATR unavailable, using fallback: ${atr.toFixed(4)}`);
            }

            const tpMultiple = settings.tpAtrMultiple || 2.0;
            const slMultiple = settings.slAtrMultiple || 1.5;

            // Calculate TP and SL prices
            let tpPrice, slPrice;
            if (isBuy) {
                // Long position: TP above, SL below
                tpPrice = entryPrice + (atr * tpMultiple);
                slPrice = entryPrice - (atr * slMultiple);
            } else {
                // Short position: TP below, SL above
                tpPrice = entryPrice - (atr * tpMultiple);
                slPrice = entryPrice + (atr * slMultiple);
            }

            // Round prices
            tpPrice = window.BrowserHyperliquidClient.roundToTickSize(tpPrice);
            slPrice = window.BrowserHyperliquidClient.roundToTickSize(slPrice);

            // console.log(`[CHASE-TRACKER] Placing TP/SL for ${asset}:`, {
            //     entry: entryPrice,
            //     atr: atr,
            //     tpMultiple: tpMultiple,
            //     slMultiple: slMultiple,
            //     tp: tpPrice,
            //     sl: slPrice,
            //     side: isBuy ? 'LONG' : 'SHORT'
            // });

            // Place TP order using proper tpslEnabled + tpPrice params
            // orderSide = entry side (buy for long), buildTPOrder flips to opposite internally
            const tpResult = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset,
                orderSide: isBuy ? 'buy' : 'sell', // Entry side - gets flipped for TP
                size: parseFloat(size),
                price: null,           // No entry price (TP/SL only mode)
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,     // Tells buildSDKOrder to use buildTPOrder
                tpPrice: tpPrice,      // TP trigger price
                slPrice: null          // Not placing SL in this call
            });

            if (tpResult.success) {
                // console.log(`[CHASE-TRACKER] ✅ TP placed at ${tpPrice}`);
            } else {
                // console.error(`[CHASE-TRACKER] ❌ Failed to place TP:`, tpResult.error);
            }

            // Place SL order using proper tpslEnabled + slPrice params
            // orderSide = entry side (buy for long), buildSLOrder flips to opposite internally
            const slResult = await window.BrowserHyperliquidClient.executeTradingOrder({
                asset,
                orderSide: isBuy ? 'buy' : 'sell', // Entry side - gets flipped for SL
                size: parseFloat(size),
                price: null,           // No entry price (TP/SL only mode)
                orderType: 'limit',
                reduceOnly: true,
                timeInForce: 'GTC',
                tpslEnabled: true,     // Tells buildSDKOrder to use buildSLOrder
                tpPrice: null,         // Not placing TP in this call
                slPrice: slPrice       // SL trigger price
            });

            if (slResult.success) {
                // console.log(`[CHASE-TRACKER] ✅ SL placed at ${slPrice}`);
            } else {
                // console.error(`[CHASE-TRACKER] ❌ Failed to place SL:`, slResult.error);
            }

            // Notify user
            if (window.NotificationManager) {
                window.NotificationManager.showNotification({
                    type: 'success',
                    title: '✅ TP/SL Placed',
                    message: `${asset}: TP=${tpPrice.toFixed(2)}, SL=${slPrice.toFixed(2)}`,
                    duration: 5000
                });
            }

        } catch (error) {
            // console.error('[CHASE-TRACKER] Error in placeTPSLOnFill:', error);
        }
    }

    /**
     * Get random interval from frequency range (in milliseconds)
     */
    getRandomInterval(settings) {
        const min = settings.frequencyRangeMin * 1000;
        const max = settings.frequencyRangeMax * 1000;
        return Math.random() * (max - min) + min;
    }

    /**
     * Calculate tick size from orderbook prices
     * Uses actual price precision from the orderbook data
     */
    calculateTickSizeFromOrderbook(orderbookData) {
        if (!orderbookData || !orderbookData.bestBid || !orderbookData.bestAsk) {
            return null;
        }
        
        // Get the number of decimal places from the best bid/ask
        const bidStr = String(orderbookData.bestBid);
        const askStr = String(orderbookData.bestAsk);
        
        // Count decimals in both prices
        const bidDecimals = bidStr.includes('.') ? bidStr.split('.')[1].length : 0;
        const askDecimals = askStr.includes('.') ? askStr.split('.')[1].length : 0;
        
        // Use the maximum decimal places
        const decimals = Math.max(bidDecimals, askDecimals);
        
        // Tick size = 10^-decimals
        const tickSize = Math.pow(10, -decimals);
        
        // console.log(`[CHASE-TRACKER] Tick size calculated from orderbook: $${tickSize} (${decimals} decimals)`);
        // console.log(`[CHASE-TRACKER]   bestBid: ${bidStr} (${bidDecimals} decimals), bestAsk: ${askStr} (${askDecimals} decimals)`);
        
        return tickSize;
    }
}

// Create singleton instance
if (typeof window !== 'undefined') {
    window.BrowserChaseTracker = new BrowserChaseTracker();
    // console.log('✅ BrowserChaseTracker initialized');
}
